import * as runtime from '@/api/runtime'
import {
  Alert,
  AlertFromJSON,
  CancelCollectionReserveRequest,
  CancelCollectionReserveRequestToJSON,
  CollectionForwardingRequestAssign,
  CollectionForwardingRequestAssignFromJSON,
  CollectionReserveProcessInfo,
  CollectionReserveProcessInfoFromJSON,
  DeleteCollectionReserveRequest,
  DeleteCollectionReserveRequestToJSON,
  DeleteReserveRequest,
  DeleteReserveRequestToJSON,
  ForwardingRequest,
  ForwardingRequestCheckRequest,
  ForwardingRequestCheckRequestToJSON,
  ForwardingRequestRequestToJSON,
  ForwardingRequestUpdateRequest,
  ForwardingRequestUpdateRequestToJSON,
  GetCollectionForwardingRequestAssignRequest,
  GetCollectionForwardingRequestAssignRequestToJSON,
  GetReserveConfirmRequest,
  GetReserveConfirmRequestToJSON,
  GetReservesRequest,
  GetReservesRequestToJSON,
  PrintReserveForwardingRequestRequest,
  Reserve,
  ReserveCheckRequest,
  ReserveCheckRequestToJSON,
  ReserveContact,
  ReserveContactFromJSON,
  ReserveContactInfo,
  ReserveContactInfoFromJSON,
  ReserveForwardingRequestAnswer,
  ReserveForwardingRequestAnswerFromJSON,
  ReserveForwardingRequestStatus,
  ReserveForwardingRequestStatusFromJSON,
  ReserveFromJSON,
  ReserveRankUpdateRequest,
  ReserveRankUpdateRequestToJSON,
  ReserveRequest,
  ReserveRequestToJSON,
  ReserveUpdateRequest,
  ReserveUpdateRequestToJSON,
  UpdateReserveContactCompleteRequest,
  UpdateReserveContactCompleteRequestToJSON,
  PrintReserveForwardingRequestRequestToJSON,
  GetReserveReceiptRequest,
  GetReserveReceiptRequestToJSON,
  GetReserveSetReserveRequestToJSON,
  ReserveSetReserve,
  ReserveSetReserveFromJSON,
  GetReserveSetReserveRequest,
  UpdateReserveSetReserveRequest,
  UpdateReserveSetReserveRequestToJSON,
  ReserveGroupReserve,
  ReserveGroupReserveFromJSON,
  GetReserveGroupReserveRequest,
  UpdateReserveGroupReserveRequest,
  UpdateReserveGroupReserveRequestToJSON,
  GetReserveGroupReserveRequestToJSON,
  GetPreferenceAssignReserveListRequest,
  PreferenceAssignReserveFromJSON,
  GetPreferenceAssignReserveListRequestToJSON,
  AssigningPreferenceReserveRequestToJSON,
  AssigningPreferenceReserveRequest,
  SimpleBibliographyReal,
  SimpleBibliographyRealFromJSON,
  PreferenceAssignReserve,
  PreferenceAssignCollectionFromJSON,
  PreferenceAssignCollection,
  UpdateReserveToPreparationRequestToJSON,
  UpdateReserveToPreparationRequest,
  UpdateBulkReserveExpirationRequest,
  UpdateBulkReserveExpirationRequestToJSON,
  RemoveReserveAndCollectionStatusUpdateRequestToJSON,
  RemoveReserveAndCollectionStatusUpdateRequest
} from '@/api'
import { HTTPBody, HTTPQuery } from '@/api/runtime'
import {
  ReserveReceipt,
  ReserveReceiptFromJSON
} from '@/api/models/reserve/ReserveReceipt'
import {
  ReceiptReserve,
  ReceiptReserveFromJSON
} from '@/api/models/receipt/ReceiptReserve'

export interface DeleteReserveExpirationRequest {
  id: number
  revision: number
}

export interface UpdateReserveExpirationRequest {
  id: number
  revision: number
}

export interface GetReserveContactsRequest {
  type: string
  startDate: string
  endDate: string
  targetBranchCode: string
  branchCode: string
  contactMethod: string
  contactCount: string
}

export interface UpdateReserveContactRequest {
  id: number
  revision: number
  reserveContactComplete: boolean
  reserveExpiration: string
  displayMemo: string
  _isClearReserveContactInfo: boolean
}

export interface AnswerForwardingRequestRequest {
  reserveId: number
  reserveRevision: number
  id: number // 資料ID
  collectionRevision: number
  answer: string
}

export class ReserveApi extends runtime.BaseAPI {
  /**
   * 入力条件により、予約を削除する
   * 予約削除
   */
  async deleteReserve(
    request: DeleteReserveRequest
  ): Promise<ReserveReceipt[]> {
    const bodyParameters: runtime.HTTPBody = DeleteReserveRequestToJSON(request)

    const response = await this.requestDelete(`/reserves`, bodyParameters)
    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveReceiptFromJSON)
    ).value()
  }

  /**
   * 入力条件により、予約の有効期限を削除する
   * 予約有効期限削除
   */
  async deleteReserveExpiration(
    request: DeleteReserveExpirationRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: runtime.HTTPBody = {
      revision: request.revision
    }

    const response = await this.requestDelete(
      `/reserves/${request.id}/reserve-expiration`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 入力条件により、予約の有効期限を更新する
   * 予約有効期限更新
   */
  async updateReserveExpiration(
    request: UpdateReserveExpirationRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: runtime.HTTPBody = {
      revision: request.revision
    }

    const response = await this.requestPost(
      `/reserves/${request.id}/reserve-expiration`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 入力条件により、予約の有効期限を一括更新する
   * 予約有効期限一括更新
   */
  async updateBulkReserveExpiration(
    request: UpdateBulkReserveExpirationRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = UpdateBulkReserveExpirationRequestToJSON(request)

    const response = await this.requestPut(
      '/reserves/bulk-update-reserve-expiration',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 予約連絡票データ取得
   */
  async reserveReceipt(id: number): Promise<ReceiptReserve> {
    const response = await this.requestPost(`/reserves/${id}/reserve-receipt`)

    return new runtime.JSONApiResponse(response, jsonValue =>
      ReserveReceiptFromJSON(jsonValue)
    ).value()
  }

  /**
   * 入力条件により、予約の有効期限を削除する
   * 予約有効期限削除
   */
  async deleteReserveCancel(id: number): Promise<runtime.VoidApiResponse> {
    const response = await this.requestDelete(`/reserve-cancels/${id}`)
    return new runtime.VoidApiResponse(response)
  }

  /**
   * 入力条件により、予約連絡を取得する
   * 予約連絡取得
   */
  async getReserveContacts(
    request: GetReserveContactsRequest
  ): Promise<ReserveContact[]> {
    const queryParameters: runtime.HTTPQuery = {}

    if (request.type !== '') {
      queryParameters.type = request.type
    }

    if (request.startDate !== '') {
      queryParameters.start_date = request.startDate
    }

    if (request.endDate !== '') {
      queryParameters.end_date = request.endDate
    }

    if (request.targetBranchCode !== '') {
      queryParameters.target_branch_code = request.targetBranchCode
    }

    if (request.branchCode !== '') {
      queryParameters.branch_code = request.branchCode
    }

    if (request.contactMethod !== '') {
      queryParameters.contact_method = request.contactMethod
    }

    if (request.contactCount !== '') {
      queryParameters.contact_count = request.contactCount
    }

    const response = await this.requestGet('/reserve-contacts', queryParameters)
    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveContactFromJSON)
    ).value()
  }

  /**
   * 入力条件により、予約連絡状態を更新する
   * 予約連絡状態更新
   */
  async updateReserveContactComplete(
    request: UpdateReserveContactCompleteRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: runtime.HTTPBody = UpdateReserveContactCompleteRequestToJSON(
      request
    )

    const response = await this.requestPut(
      '/reserves/contact-complete',
      bodyParameters
    )
    return new runtime.VoidApiResponse(response)
  }

  /**
   * 予約チェックを行う
   * 予約チェック
   */
  async reserveCheck(request: ReserveCheckRequest): Promise<Alert[]> {
    const response = await this.requestPost(
      `/reserves/check`,
      ReserveCheckRequestToJSON(request)
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(AlertFromJSON)
    ).value()
  }

  async reserve(request: ReserveRequest): Promise<runtime.VoidApiResponse> {
    const response = await this.requestPost(
      `/reserves`,
      ReserveRequestToJSON(request)
    )

    return new runtime.VoidApiResponse(response)
  }

  async reserveUpdate(request: ReserveUpdateRequest): Promise<ReserveReceipt> {
    const response = await this.requestPut(
      `/reserves/${request.id}`,
      ReserveUpdateRequestToJSON(request)
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ReserveReceiptFromJSON(jsonValue)
    ).value()
  }

  async forwardingRequestCheck(
    request: ForwardingRequestCheckRequest
  ): Promise<boolean> {
    const response = await this.requestPost(
      `/reserves/forwarding-request-check`,
      ForwardingRequestCheckRequestToJSON(request)
    )

    return new runtime.JSONApiResponse(
      response,
      jsonValue => jsonValue.can_forwarding_request
    ).value()
  }

  async forwardingRequest(
    request: ForwardingRequest
  ): Promise<runtime.VoidApiResponse> {
    const response = await this.requestPost(
      `/reserves/forwarding-request`,
      ForwardingRequestRequestToJSON(request)
    )

    return new runtime.VoidApiResponse(response)
  }

  async forwardingRequestUpdate(
    request: ForwardingRequestUpdateRequest
  ): Promise<runtime.VoidApiResponse> {
    const response = await this.requestPut(
      `/reserves/${request.id}/forwarding-request`,
      ForwardingRequestUpdateRequestToJSON(request)
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 入力条件により、予約連絡情報を取得する
   * 予約情報を取得
   */
  async getReserveContactInfo(id: number): Promise<ReserveContactInfo> {
    const response = await this.requestGet(`/reserves/${id}`)
    return new runtime.JSONApiResponse(response, jsonValue =>
      ReserveContactInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 入力条件により、予約連絡状況を更新する
   * 予約連絡状況更新
   */
  async updateReserveContact(
    request: UpdateReserveContactRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = {
      id: request.id,
      revision: request.revision,
      reserve_contact_complete: request.reserveContactComplete,
      _is_clear_reserve_contact_info: request._isClearReserveContactInfo
    }

    if (request.reserveExpiration !== '') {
      bodyParameters.reserve_expiration = request.reserveExpiration
    }

    if (request.displayMemo !== '') {
      bodyParameters.display_memo = request.displayMemo
    }

    const response = await this.requestPut(
      `/reserves/${request.id}/contact`,
      bodyParameters
    )
    return new runtime.VoidApiResponse(response)
  }

  /**
   * 回送依頼状況を取得する
   * 回送依頼状況取得
   */
  async getReserveForwardingRequestAnswers(): Promise<
    ReserveForwardingRequestAnswer[]
  > {
    const response = await this.requestGet(
      '/reserves/forwarding-request-answer'
    )
    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveForwardingRequestAnswerFromJSON)
    ).value()
  }

  /**
   * 資料回送依頼情報取得
   * 資料回送依頼情報を取得する
   */
  async getCollectionForwardingRequestAssign(
    request: GetCollectionForwardingRequestAssignRequest
  ): Promise<CollectionForwardingRequestAssign> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetCollectionForwardingRequestAssignRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      `/reserves/forwarding-request-assign`,
      queryParameters
    )
    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionForwardingRequestAssignFromJSON(jsonValue)
    ).value()
  }

  /**
   * 回送依頼を回答する
   * 回送依頼回答
   */
  async answerForwardingRequest(
    requests: AnswerForwardingRequestRequest[]
  ): Promise<ReceiptReserve[]> {
    const bodyParameters: HTTPBody = { answer: [] }

    requests.forEach((request, index) => {
      bodyParameters.answer[index] = {
        reserve_id: request.reserveId,
        reserve_revision: request.reserveRevision,
        answer: request.answer
      }

      if (request.id !== 0) {
        bodyParameters.answer[index].id = request.id
      }

      if (request.collectionRevision !== 0) {
        bodyParameters.answer[index].collection_revision =
          request.collectionRevision
      }
    })

    const response = await this.requestPost(
      '/reserves/forwarding-request-answer',
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReceiptReserveFromJSON)
    ).value()
  }

  /**
   * 回送依頼状況を取得する
   * 回送依頼状況取得
   */
  async getReserveForwardingRequestStatuses(): Promise<
    ReserveForwardingRequestStatus[]
  > {
    const response = await this.requestGet(
      '/reserves/forwarding-request-status'
    )
    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveForwardingRequestStatusFromJSON)
    ).value()
  }
  /**
   * 入力条件により、資料予約の削除を行う
   * 資料予約削除
   */
  async deleteCollectionReserve(
    request: DeleteCollectionReserveRequest
  ): Promise<CollectionReserveProcessInfo> {
    const bodyParameters: HTTPBody = DeleteCollectionReserveRequestToJSON(
      request
    )

    const response = await this.requestDelete(
      `/reserves/collection`,
      bodyParameters
    )
    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionReserveProcessInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 入力条件により、資料予約を解除する
   * 資料予約解除
   */
  async cancelCollectionReserve(
    request: CancelCollectionReserveRequest
  ): Promise<CollectionReserveProcessInfo> {
    const bodyParameters: HTTPBody = CancelCollectionReserveRequestToJSON(
      request
    )
    const response = await this.requestPut(
      `/reserves/collection/cancel`,
      bodyParameters
    )
    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionReserveProcessInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 入力条件により、資料予約確認情報を取得する
   * 資料予約確認情報取得
   */
  async getReserveConfirm(
    request: GetReserveConfirmRequest
  ): Promise<CollectionReserveProcessInfo> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetReserveConfirmRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      `/reserves/reserve-confirm`,
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionReserveProcessInfoFromJSON(jsonValue)
    ).value()
  }

  async updateReserveRank(
    request: ReserveRankUpdateRequest
  ): Promise<runtime.VoidApiResponse> {
    const response = await this.requestPut(
      `/reserves/rank`,
      ReserveRankUpdateRequestToJSON(request)
    )
    return new runtime.VoidApiResponse(response)
  }

  /**
   *  予約一覧を取得する
   *  予約一覧取得
   */
  async getReserves(request: GetReservesRequest): Promise<Reserve[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetReservesRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(`/reserves`, queryParameters)

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.reserves.map(ReserveFromJSON)
    ).value()
  }

  /**
   * 回送依頼票を出力する
   * 回送依頼票出力
   */

  async printReserveForwardingRequest(
    request: PrintReserveForwardingRequestRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = PrintReserveForwardingRequestRequestToJSON(request)

    const response = await this.requestPost(
      '/reserves/forwarding-request-print',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 予約連絡票レシート出力値を取得する
   * 予約連絡票レシート出力値取得
   */

  async getReserveReceipt(
    request: GetReserveReceiptRequest
  ): Promise<ReserveReceipt[]> {
    const bodyParameters = GetReserveReceiptRequestToJSON(request)

    const response = await this.requestPost(
      '/reserves/reserve-receipt',
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveReceiptFromJSON)
    ).value()
  }

  /**
   * セット予約取得
   * セット予約を取得する
   */
  async getReserveSetReserve(
    request: GetReserveSetReserveRequest
  ): Promise<ReserveSetReserve[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetReserveSetReserveRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      `/reserves/reserve-sets`,
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveSetReserveFromJSON)
    ).value()
  }

  /**
   * セット予約更新
   * セット予約を更新する
   */
  async updateReserveSetReserve(
    request: UpdateReserveSetReserveRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = UpdateReserveSetReserveRequestToJSON(request)

    const response = await this.requestPost(
      '/reserves/reserve-sets',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * グループ予約取得
   * グループ予約を取得する
   */
  async getReserveGroupReserve(
    request: GetReserveGroupReserveRequest
  ): Promise<ReserveGroupReserve[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetReserveGroupReserveRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      `/reserves/reserve-groups`,
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReserveGroupReserveFromJSON)
    ).value()
  }

  /**
   * グループ予約更新
   * グループ予約を更新する
   */
  async updateReserveGroupReserve(
    request: UpdateReserveGroupReserveRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = UpdateReserveGroupReserveRequestToJSON(request)

    const response = await this.requestPost(
      '/reserves/reserve-groups',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 優先割当予約一覧取得
   * 優先割当予約一覧を取得する
   */
  async getPreferenceAssignReserveList(
    request: GetPreferenceAssignReserveListRequest
  ): Promise<{
    bibliography: SimpleBibliographyReal
    collection: PreferenceAssignCollection
    reserves: PreferenceAssignReserve[]
  }> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetPreferenceAssignReserveListRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      `/reserves/preference-assign`,
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue => {
      return {
        bibliography: SimpleBibliographyRealFromJSON(jsonValue.bibliography),
        collection: PreferenceAssignCollectionFromJSON(jsonValue.collection),
        reserves: jsonValue.reserves.map(PreferenceAssignReserveFromJSON)
      }
    }).value()
  }

  /**
   * 予約優先割当
   * 予約を優先割当する
   */
  async assigningPreferenceReserve(
    request: AssigningPreferenceReserveRequest
  ): Promise<ReserveReceipt> {
    const bodyParameters = AssigningPreferenceReserveRequestToJSON(request)

    const response = await this.requestPost(
      '/reserves/preference-assign',
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ReserveReceiptFromJSON(jsonValue)
    ).value()
  }

  /**
   * 予約受取準備中更新
   * 予約を受取準備中に更新する
   */
  async updateReserveToPreparation(
    request: UpdateReserveToPreparationRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = UpdateReserveToPreparationRequestToJSON(request)

    const response = await this.requestPut(
      `/reserves/${bodyParameters.id}/preparation`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 予約削除及び資料状態更新
   * 予約の削除及び資料状態を更新する
   */
  async removeReserveAndCollectionStatusUpdate(
    request: RemoveReserveAndCollectionStatusUpdateRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters = RemoveReserveAndCollectionStatusUpdateRequestToJSON(
      request
    )

    const response = await this.requestDelete(
      `/reserves/${bodyParameters.id}/remove_and_collection_status_update`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }
}
